import { createFileRoute, redirect } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';

import { getApiClient } from '@apps/web/lib/api_client';
import { EVENTS } from '@apps/web/lib/events';
import { sanitizeReturnToPath } from '@apps/web/lib/post_login_redirect';
import { sameOriginRedirectTarget } from '@apps/web/lib/same_origin_redirect';
import { ApiError } from '@libraries/typescript/api_client';

function returnToPathForCurrentOrigin(href: string): string {
    return sanitizeReturnToPath(href, window.location.origin) ?? '/';
}

export const Route = createFileRoute('/r/$code')({
    beforeLoad: ({ context, location }) => {
        // Carry the link's path through sign-in so a logged-out tap returns
        // here after authenticating, instead of landing on home. Mirrors the
        // _app guard; sign-in sanitizes the value and Kratos allowlists it.
        if (!context.sessionState?.session) {
            throw redirect({
                to: '/sign-in',
                search: {
                    return_to: returnToPathForCurrentOrigin(location.href),
                },
            });
        }
    },
    loader: ({ params }) =>
        getApiClient().get({
            url: '/short-links/{code}',
            pathParams: { code: params.code },
        }),
    component: ShortLinkRedirect,
    errorComponent: ShortLinkError,
});

function ShortLinkRedirect() {
    const resolution = Route.useLoaderData();
    const posthog = usePostHog();
    const redirected = useRef(false);
    useEffect(() => {
        if (redirected.current) {
            return;
        }
        // Never hand a raw value to window.location — only a re-resolved
        // same-origin URL, so the destination can't become an open redirect.
        const redirect = () => {
            if (redirected.current) {
                return;
            }
            redirected.current = true;
            window.location.href = sameOriginRedirectTarget(
                resolution.destination_path,
                window.location.origin,
            );
        };
        // The SMS link carries no UTM, so fire attribution here. usePostHog()
        // can be null until posthog-js initializes, so wait for it rather than
        // dropping the event — but never block the redirect: a fallback fires
        // regardless, and the server-stamped opened_at is the authoritative
        // funnel record either way.
        if (posthog) {
            try {
                posthog.capture(
                    EVENTS.SHORT_LINK_OPENED,
                    {
                        channel: resolution.channel,
                        campaign: resolution.campaign,
                        utm_source: 'phoebe',
                        utm_medium: resolution.channel,
                        utm_campaign: resolution.campaign,
                        organization_id: resolution.organization_id,
                    },
                    { send_instantly: true },
                );
            } catch {
                // Supplementary analytics only.
            }
            redirect();
            return;
        }
        const fallback = setTimeout(redirect, 2000);
        return () => clearTimeout(fallback);
    }, [resolution, posthog]);
    return (
        <div className="bg-background text-muted-foreground flex min-h-screen items-center justify-center">
            Opening…
        </div>
    );
}

function ShortLinkError({ error }: { readonly error: Error }) {
    // A genuinely gone link reads as "no longer available"; an expired session
    // (mid-flight 401) should re-auth, not read as broken; anything else is
    // transient and should invite a retry rather than claim the link is gone.
    const status = error instanceof ApiError ? error.status : undefined;
    useEffect(() => {
        if (status === 401) {
            window.location.href = '/sign-in';
        }
    }, [status]);
    let message = 'Something went wrong opening this link. Please try again.';
    if (status === 404) {
        message = 'This link is no longer available.';
    } else if (status === 401) {
        message = 'Signing you in…';
    }
    return (
        <div className="bg-background text-muted-foreground flex min-h-screen items-center justify-center">
            {message}
        </div>
    );
}
